/**
 * Document Capture Utility Functions
 *
 * Comprehensive utility functions for document image processing and capture.
 * Handles complex video-to-image coordinate transformations, cropping operations,
 * and document quality optimization.
 *
 * Key Features:
 * - Video scaling calculations for object-fit: cover behavior
 * - Coordinate transformation between display and actual video dimensions
 * - Guide zone-based cropping with configurable buffer
 * - High-quality JPEG compression and encoding
 * - Aspect ratio handling for various camera orientations
 * - Bounds checking for safe cropping operations
 *
 * The utilities handle the mathematical complexity of mapping guide zones
 * from the UI display coordinates to actual video frame coordinates,
 * accounting for aspect ratio differences and scaling factors.
 *
 * @author SafePassage Engineering
 * @version 1.0.0
 */

import { CAMERA_PREVIEW, QUALITY } from '../constants/capture';
import type { DocumentCaptureDiagnostics } from '../types/verification';

/**
 * Video scaling information for coordinate transformation
 *
 * @interface VideoScaling
 * @property {number} scaleX - X-axis scale factor
 * @property {number} scaleY - Y-axis scale factor
 * @property {number} offsetX - X-axis offset for centering
 * @property {number} offsetY - Y-axis offset for centering
 */
interface VideoScaling {
  scaleX: number;
  scaleY: number;
  offsetX: number;
  offsetY: number;
}

/**
 * Crop dimensions for image processing
 *
 * @interface CropDimensions
 * @property {number} x - X coordinate of crop area
 * @property {number} y - Y coordinate of crop area
 * @property {number} width - Width of crop area
 * @property {number} height - Height of crop area
 */
interface CropDimensions {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Guide zone definition for document capture
 *
 * @interface GuideZone
 * @property {number} top - Top position in display coordinates
 * @property {number} left - Left position in display coordinates
 * @property {number} width - Width in display coordinates
 * @property {number} height - Height in display coordinates
 */
interface GuideZone {
  top: number;
  left: number;
  width: number;
  height: number;
}

interface ImageDimensions {
  width: number;
  height: number;
}

const DOCUMENT_UPLOAD_MAX_LONG_EDGE = 1536;
const DOCUMENT_UPLOAD_JPEG_QUALITY = 0.88;
/**
 * Upper bound on the encoded document image. Production reaches Verify Engine
 * through nginx's default 1 MiB request body limit, which the selfie, form
 * fields and multipart framing share, so the document keeps over 300 KB clear
 * for them. An image over the limit was refused (413) before Engine saw it,
 * and a retake of the same scene was refused again.
 */
export const DOCUMENT_UPLOAD_MAX_BYTES = 700_000;
/** Lower JPEG qualities tried, in order, only while the image is over budget. */
const DOCUMENT_UPLOAD_FALLBACK_QUALITIES = [0.8, 0.72, 0.64];

/** Decoded size of a base64 data URL. */
export function encodedImageBytes(dataUrl: string): number {
  const comma = dataUrl.indexOf(',');
  const base64 = comma >= 0 ? dataUrl.slice(comma + 1) : dataUrl;
  const padding = base64.endsWith('==') ? 2 : base64.endsWith('=') ? 1 : 0;
  return Math.floor((base64.length * 3) / 4) - padding;
}

export interface ProcessedDocumentImageResult {
  imageData: string;
  diagnostics: Partial<DocumentCaptureDiagnostics>;
}

/**
 * Get video dimensions from HTMLVideoElement
 *
 * Extracts both display dimensions (how video appears on screen) and
 * actual dimensions (native video resolution) for scaling calculations.
 *
 * @param {HTMLVideoElement} video - Video element to measure
 * @returns {Object} Video dimension information
 * @returns {number} returns.displayWidth - Display width in pixels
 * @returns {number} returns.displayHeight - Display height in pixels
 * @returns {number} returns.actualWidth - Actual video width in pixels
 * @returns {number} returns.actualHeight - Actual video height in pixels
 */
function getVideoDimensions(video: HTMLVideoElement) {
  return {
    displayWidth: video.offsetWidth,
    displayHeight: video.offsetHeight,
    actualWidth: video.videoWidth,
    actualHeight: video.videoHeight,
  };
}

/**
 * Calculate aspect ratios for video scaling
 *
 * Computes aspect ratios for both display and actual video dimensions
 * to determine scaling behavior.
 *
 * @param {ReturnType<typeof getVideoDimensions>} dimensions - Video dimensions
 * @returns {Object} Aspect ratio information
 * @returns {number} returns.display - Display aspect ratio
 * @returns {number} returns.actual - Actual video aspect ratio
 */
function calculateAspectRatios(
  dimensions: ReturnType<typeof getVideoDimensions>
) {
  return {
    display: dimensions.displayWidth / dimensions.displayHeight,
    actual: dimensions.actualWidth / dimensions.actualHeight,
  };
}

/**
 * Calculate scale factors based on aspect ratio comparison
 *
 * Determines scaling factors needed to achieve object-fit: cover behavior,
 * where video fills the display area while maintaining aspect ratio.
 *
 * @param {ReturnType<typeof getVideoDimensions>} dimensions - Video dimensions
 * @param {ReturnType<typeof calculateAspectRatios>} aspectRatios - Aspect ratios
 * @returns {Object} Scale factors
 * @returns {number} returns.scaleX - X-axis scale factor
 * @returns {number} returns.scaleY - Y-axis scale factor
 */
function calculateScaleFactors(
  dimensions: ReturnType<typeof getVideoDimensions>,
  aspectRatios: ReturnType<typeof calculateAspectRatios>
) {
  const isVideoWiderThanDisplay = aspectRatios.actual > aspectRatios.display;

  if (isVideoWiderThanDisplay) {
    // Video height fills display height
    const scale = dimensions.actualHeight / dimensions.displayHeight;
    return { scaleX: scale, scaleY: scale };
  } else {
    // Video width fills display width
    const scale = dimensions.actualWidth / dimensions.displayWidth;
    return { scaleX: scale, scaleY: scale };
  }
}

/**
 * Calculate offsets for centered video display
 *
 * Calculates the offset needed to center the video within the display area
 * when using object-fit: cover behavior.
 *
 * @param {ReturnType<typeof getVideoDimensions>} dimensions - Video dimensions
 * @param {Object} scales - Scale factors
 * @param {number} scales.scaleX - X-axis scale factor
 * @param {number} scales.scaleY - Y-axis scale factor
 * @param {ReturnType<typeof calculateAspectRatios>} aspectRatios - Aspect ratios
 * @returns {Object} Offset values
 * @returns {number} returns.offsetX - X-axis offset
 * @returns {number} returns.offsetY - Y-axis offset
 */
function calculateOffsets(
  dimensions: ReturnType<typeof getVideoDimensions>,
  scales: { scaleX: number; scaleY: number },
  aspectRatios: ReturnType<typeof calculateAspectRatios>
) {
  const isVideoWiderThanDisplay = aspectRatios.actual > aspectRatios.display;

  if (isVideoWiderThanDisplay) {
    // Center horizontally
    const offsetX =
      (dimensions.actualWidth - dimensions.displayWidth * scales.scaleX) / 2;
    return { offsetX, offsetY: 0 };
  } else {
    // Center vertically
    const offsetY =
      (dimensions.actualHeight - dimensions.displayHeight * scales.scaleY) / 2;
    return { offsetX: 0, offsetY };
  }
}

/**
 * Calculate video scaling factors for object-fit: cover behavior
 *
 * Main function that computes all scaling information needed to transform
 * coordinates from display space to video frame space. Handles the complex
 * math of object-fit: cover behavior.
 *
 * @param {HTMLVideoElement} video - Video element to analyze
 * @returns {VideoScaling} Complete scaling information
 * @export
 */
export function calculateVideoScaling(video: HTMLVideoElement): VideoScaling {
  const dimensions = getVideoDimensions(video);
  const aspectRatios = calculateAspectRatios(dimensions);
  const scales = calculateScaleFactors(dimensions, aspectRatios);
  const offsets = calculateOffsets(dimensions, scales, aspectRatios);

  return {
    ...scales,
    ...offsets,
  };
}

/**
 * Calculate buffer size for crop area
 *
 * Computes buffer dimensions to expand the crop area beyond the guide zone
 * for better document edge capture.
 *
 * @param {GuideZone} guideZone - Guide zone definition
 * @param {number} bufferFactor - Buffer factor (0.0 to 1.0)
 * @returns {Object} Buffer dimensions
 * @returns {number} returns.x - X-axis buffer size
 * @returns {number} returns.y - Y-axis buffer size
 */
function calculateBuffer(guideZone: GuideZone, bufferFactor: number) {
  return {
    x: guideZone.width * bufferFactor,
    y: guideZone.height * bufferFactor,
  };
}

/**
 * Calculate raw crop coordinates before bounds checking
 *
 * Computes the initial crop coordinates by transforming guide zone coordinates
 * to video frame coordinates and applying buffer expansion.
 *
 * @param {GuideZone} guideZone - Guide zone definition
 * @param {Object} buffer - Buffer dimensions
 * @param {number} buffer.x - X-axis buffer size
 * @param {number} buffer.y - Y-axis buffer size
 * @param {VideoScaling} scaling - Video scaling information
 * @returns {Object} Raw crop coordinates
 * @returns {number} returns.x - X coordinate
 * @returns {number} returns.y - Y coordinate
 * @returns {number} returns.width - Width
 * @returns {number} returns.height - Height
 */
function calculateRawCropCoordinates(
  guideZone: GuideZone,
  buffer: { x: number; y: number },
  scaling: VideoScaling
) {
  return {
    x: (guideZone.left - buffer.x) * scaling.scaleX + scaling.offsetX,
    y: (guideZone.top - buffer.y) * scaling.scaleY + scaling.offsetY,
    width: (guideZone.width + buffer.x * 2) * scaling.scaleX,
    height: (guideZone.height + buffer.y * 2) * scaling.scaleY,
  };
}

/**
 * Apply bounds checking to crop dimensions
 *
 * Ensures crop dimensions are within video frame bounds to prevent
 * out-of-bounds errors during image processing.
 *
 * @param {ReturnType<typeof calculateRawCropCoordinates>} rawCrop - Raw crop coordinates
 * @param {number} videoWidth - Video frame width
 * @param {number} videoHeight - Video frame height
 * @returns {CropDimensions} Bounds-checked crop dimensions
 */
function applyBoundsChecking(
  rawCrop: ReturnType<typeof calculateRawCropCoordinates>,
  videoWidth: number,
  videoHeight: number
): CropDimensions {
  const x = Math.max(0, rawCrop.x);
  const y = Math.max(0, rawCrop.y);
  const width = Math.min(videoWidth - x, rawCrop.width);
  const height = Math.min(videoHeight - y, rawCrop.height);

  return { x, y, width, height };
}

/**
 * Calculate crop dimensions based on guide zone and video scaling
 *
 * Main function that combines all cropping calculations to determine the final
 * crop dimensions for extracting document image from video frame.
 *
 * @param {GuideZone} guideZone - Guide zone definition
 * @param {VideoScaling} scaling - Video scaling information
 * @param {number} videoWidth - Video frame width
 * @param {number} videoHeight - Video frame height
 * @param {number} [bufferFactor] - Buffer factor for crop expansion
 * @returns {CropDimensions} Final crop dimensions
 * @export
 */
export function calculateCropDimensions(
  guideZone: GuideZone,
  scaling: VideoScaling,
  videoWidth: number,
  videoHeight: number,
  bufferFactor: number = CAMERA_PREVIEW.CAPTURE_BUFFER_FACTOR
): CropDimensions {
  const buffer = calculateBuffer(guideZone, bufferFactor);
  const rawCrop = calculateRawCropCoordinates(guideZone, buffer, scaling);
  return applyBoundsChecking(rawCrop, videoWidth, videoHeight);
}

export function createDocumentProcessingDiagnostics(
  fullFrameWidth: number | null,
  fullFrameHeight: number | null,
  cropDimensions: CropDimensions | null,
  finalImageDimensions: ImageDimensions | null = null
): Partial<DocumentCaptureDiagnostics> {
  if (!cropDimensions) {
    return {
      cropApplied: false,
      cropSourceX: null,
      cropSourceY: null,
      cropSourceWidth: null,
      cropSourceHeight: null,
      finalImageWidth: fullFrameWidth,
      finalImageHeight: fullFrameHeight,
    };
  }

  return {
    cropApplied: true,
    cropSourceX: cropDimensions.x,
    cropSourceY: cropDimensions.y,
    cropSourceWidth: cropDimensions.width,
    cropSourceHeight: cropDimensions.height,
    finalImageWidth: finalImageDimensions?.width ?? cropDimensions.width,
    finalImageHeight: finalImageDimensions?.height ?? cropDimensions.height,
  };
}

function getResizedImageDimensions(
  width: number,
  height: number,
  maxLongEdge: number = DOCUMENT_UPLOAD_MAX_LONG_EDGE
): ImageDimensions {
  const longEdge = Math.max(width, height);

  if (longEdge <= maxLongEdge) {
    return { width, height };
  }

  const scale = maxLongEdge / longEdge;

  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

/**
 * Crop an image to specified dimensions
 *
 * Performs the actual image cropping operation using HTML5 Canvas.
 * Creates a new canvas, draws the cropped portion, and returns as base64 JPEG
 * within the upload budget.
 *
 * @param {string} sourceImageData - Base64 encoded source image
 * @param {CropDimensions | null} cropDimensions - Crop area dimensions; null keeps the full image
 * @param {number} [quality] - JPEG quality (0-1)
 * @returns {Promise<string>} Promise resolving to base64 encoded cropped image
 * @export
 */
export async function cropImage(
  sourceImageData: string,
  requestedCrop: CropDimensions | null,
  quality: number = QUALITY.IMAGE_COMPRESSION_QUALITY
): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();

    img.onload = () => {
      const cropDimensions = requestedCrop ?? {
        x: 0,
        y: 0,
        width: img.naturalWidth || img.width,
        height: img.naturalHeight || img.height,
      };
      const resizedDimensions = getResizedImageDimensions(
        cropDimensions.width,
        cropDimensions.height
      );
      const canvas = document.createElement('canvas');
      canvas.width = resizedDimensions.width;
      canvas.height = resizedDimensions.height;
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        reject(new Error('Failed to get canvas context'));
        return;
      }

      ctx.drawImage(
        img,
        cropDimensions.x,
        cropDimensions.y,
        cropDimensions.width,
        cropDimensions.height,
        0,
        0,
        resizedDimensions.width,
        resizedDimensions.height
      );

      let encoded = canvas.toDataURL('image/jpeg', quality);
      for (const lower of DOCUMENT_UPLOAD_FALLBACK_QUALITIES) {
        if (encodedImageBytes(encoded) <= DOCUMENT_UPLOAD_MAX_BYTES) break;
        if (lower < quality) encoded = canvas.toDataURL('image/jpeg', lower);
      }
      resolve(encoded);
    };

    img.onerror = () => reject(new Error('Failed to load image for cropping'));
    img.src = sourceImageData;
  });
}

/**
 * Process captured document image
 *
 * Main document processing function that handles the complete pipeline from
 * full video frame to cropped document image. Applies guide zone cropping
 * if specified, with fallback to original image on errors.
 *
 * @param {string} fullImageData - Base64 encoded full image
 * @param {HTMLVideoElement | null} video - Video element for scaling calculations
 * @param {GuideZone | null} guideZone - Guide zone for cropping
 * @returns {Promise<string>} Promise resolving to processed image
 * @export
 */
export async function processDocumentImage(
  fullImageData: string,
  video: HTMLVideoElement | null,
  guideZone: GuideZone | null,
  fullFrameWidth: number | null = null,
  fullFrameHeight: number | null = null
): Promise<ProcessedDocumentImageResult> {
  // Without a crop the full frame is uploaded, but never over the budget:
  // an oversized frame is resized and re-encoded like a crop.
  const fitFullFrame = async (): Promise<string> => {
    if (encodedImageBytes(fullImageData) <= DOCUMENT_UPLOAD_MAX_BYTES) return fullImageData;
    try {
      return await cropImage(fullImageData, null, DOCUMENT_UPLOAD_JPEG_QUALITY);
    } catch {
      return fullImageData;
    }
  };

  // If no video or guide zone, upload the full frame
  if (!video || !guideZone || guideZone.width === 0) {
    return {
      imageData: await fitFullFrame(),
      diagnostics: createDocumentProcessingDiagnostics(
        fullFrameWidth,
        fullFrameHeight,
        null
      ),
    };
  }

  try {
    const scaling = calculateVideoScaling(video);
    const cropDimensions = calculateCropDimensions(
      guideZone,
      scaling,
      video.videoWidth,
      video.videoHeight
    );
    const resizedDimensions = getResizedImageDimensions(
      cropDimensions.width,
      cropDimensions.height
    );

    const imageData = await cropImage(
      fullImageData,
      cropDimensions,
      DOCUMENT_UPLOAD_JPEG_QUALITY
    );
    return {
      imageData,
      diagnostics: createDocumentProcessingDiagnostics(
        fullFrameWidth ?? video.videoWidth,
        fullFrameHeight ?? video.videoHeight,
        cropDimensions,
        resizedDimensions
      ),
    };
  } catch (error) {
    // If cropping fails, upload the full frame
    console.error('Failed to crop image:', error);
    return {
      imageData: await fitFullFrame(),
      diagnostics: createDocumentProcessingDiagnostics(
        fullFrameWidth ?? video.videoWidth,
        fullFrameHeight ?? video.videoHeight,
        null
      ),
    };
  }
}

/**
 * Create a captured document object
 *
 * Creates a structured document object with image data and metadata.
 *
 * @param {string} imageData - Base64 encoded image data
 * @returns {Object} Captured document object
 * @returns {string} returns.image - Base64 encoded image
 * @returns {string} returns.timestamp - ISO timestamp of capture
 * @export
 */
export function createCapturedDocument(
  imageData: string,
  captureDiagnostics?: DocumentCaptureDiagnostics
) {
  return {
    image: imageData,
    timestamp: new Date().toISOString(),
    captureDiagnostics,
  };
}
